import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react';
import { RenderField, RenderFieldProps } from './RenderField';
import {
  RenderAffiliationData,
  RenderAffiliationDataProps
} from './RenderAffiliationData';
import {
  IResearchSpecialtyKeyword,
  RenderResearchSpecialtyKeyword
} from './RenderResearchSpecialtyKeyword';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp, faCircle } from '@fortawesome/pro-solid-svg-icons';
import { Badge, OutlineCard } from '@morressier/alembic';
import {
  IAffiliationDetails,
  IUserProfileAuditLog,
  IUserProfileAuditLogAction
} from '@publishing-engine/domain/models';
import RenderProfileData, { RenderProfileDataProps } from './RenderProfileData';
import { format } from 'date-fns';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import DuplicateAccounts from '../DuplicateAccounts';

type TimelineStepProps = {
  expanded: boolean;
  isLast?: boolean;
  onToggle: () => void;
  log: IUserProfileAuditLog;
};

type ComponentMap = {
  firstName: React.ComponentType<RenderFieldProps>;
  degree: React.ComponentType<RenderFieldProps>;
  lastName: React.ComponentType<RenderFieldProps>;
  middleName: React.ComponentType<RenderFieldProps>;
  orcid: React.ComponentType<RenderFieldProps>;
  secondaryEmail: React.ComponentType<RenderFieldProps>;
  ccEmail: React.ComponentType<RenderFieldProps>;
  jobTitle: React.ComponentType<RenderFieldProps>;
  affiliationDetails: React.ComponentType<RenderAffiliationDataProps>;
  profileDetails: React.ComponentType<RenderProfileDataProps>;
  additionalAffiliations: React.ComponentType<RenderAffiliationDataProps>;
  affiliation: React.ComponentType<RenderFieldProps>;
  keyword: React.ComponentType<RenderFieldProps>;
  scandinavianKeyword: React.ComponentType<RenderFieldProps>;
  userName: React.ComponentType<RenderFieldProps>;
};
const USER_PROFILE_AUDIT_LOG_ACTION_LABELS: Record<
  IUserProfileAuditLogAction,
  string
> = {
  PROFILE_CREATED: 'Profile Created',
  PROFILE_CREATED_BY_ADMIN: 'Profile Created',
  PROFILE_UPDATED: 'Profile Updated',
  PROFILE_UPDATED_BY_ADMIN: 'Profile Updated',
  PROFILE_MERGED: 'Merge Activity',
  JOURNAL_KEYWORD_ADDED: 'Journal Specific Specialty & Keyword(s) Added',
  JOURNAL_KEYWORD_UPDATED: 'Journal Specific Specialty & Keyword(s) Updated',
  JOURNAL_KEYWORD_REMOVED: 'Journal Specific Specialty & Keyword(s) Removed'
};

const componentMap: ComponentMap = {
  firstName: RenderField,
  degree: RenderField,
  lastName: RenderField,
  middleName: RenderField,
  orcid: RenderField,
  secondaryEmail: RenderField,
  ccEmail: RenderField,
  affiliationDetails: RenderAffiliationData,
  profileDetails: RenderProfileData,
  additionalAffiliations: RenderAffiliationData,
  affiliation: RenderField,
  keyword: RenderField,
  scandinavianKeyword: RenderField,
  jobTitle: RenderField,
  userName: RenderField
};
function ChangeCells({
  children,
  hasJournalName,
  isFirst,
  isLast
}: {
  children: [React.ReactNode, React.ReactNode];
  hasJournalName: boolean;
  isFirst: boolean;
  isLast: boolean;
}) {
  const radius = !hasJournalName;
  const borderTop = isFirst ? '1px solid' : undefined;
  return (
    <Grid
      templateColumns="repeat(2, minmax(0, 1fr))"
      alignItems="stretch"
      width="100%"
    >
      {/* Previous — left cell */}
      <Box
        minW={0}
        overflow="hidden"
        p={4}
        borderTop={borderTop}
        borderRight="1px solid"
        borderBottom="1px solid"
        borderLeft="1px solid"
        borderTopLeftRadius={radius && isFirst ? 'base' : ''}
        borderBottomLeftRadius={radius && isLast ? 'base' : ''}
        borderColor="gray.300"
      >
        {children[0]}
      </Box>
      {/* Current — right cell */}
      <Box
        minW={0}
        overflow="hidden"
        p={4}
        borderTop={borderTop}
        borderRight="1px solid"
        borderBottom="1px solid"
        borderTopRightRadius={radius && isFirst ? 'base' : ''}
        borderBottomRightRadius={radius && isLast ? 'base' : ''}
        borderColor="gray.300"
      >
        {children[1]}
      </Box>
    </Grid>
  );
}

export default function TimelineStep({
  log,
  expanded,
  isLast = false,
  onToggle
}: TimelineStepProps) {
  const hasJournalName = Boolean(log?.journalName);
  const hasChanges =
    log?.changedFields?.length || log?.deactivatedAccountDetails?.length;
  return (
    <Flex align="stretch">
      {/* Timeline */}
      <Box
        position="relative"
        width="48px"
        display="flex"
        justifyContent="center"
        flexShrink={0}
      >
        {/* Vertical Line */}
        {!isLast && (
          <Box
            position="absolute"
            top="0px"
            bottom="0"
            left="50%"
            transform="translateX(-50%)"
            width="2px"
            bg="gray.300"
          />
        )}

        {/* Step Circle */}
        <Flex
          zIndex={2}
          w="24px"
          h="24px"
          borderRadius="1px"
          border="2px solid"
          borderColor="blue.500"
          color={'actionSecondaryContent'}
          backgroundColor={'actionSecondaryFill'}
          align="center"
          justify="center"
          cursor="pointer"
          onClick={onToggle}
        >
          {!hasChanges ? (
            <>
              <FontAwesomeIcon
                icon={faCircle}
                fontSize={'0.6rem'}
                style={{ color: 'var(--chakra-colors-blue-500)' }}
              />
            </>
          ) : expanded ? (
            <FontAwesomeIcon icon={faAngleUp} />
          ) : (
            <FontAwesomeIcon icon={faAngleDown} />
          )}{' '}
        </Flex>
      </Box>

      <Box flex={1} pb={8}>
        <Box pl={0} mb={4}>
          <Text as="span" textStyle={'headingItem'}>
            {' '}
            {USER_PROFILE_AUDIT_LOG_ACTION_LABELS[log.action]}
          </Text>{' '}
          on{' '}
          <Text as="span" textStyle={'headingItem'}>
            {log?.created_at &&
              format(new Date(log?.created_at), 'MMM dd, yyyy')}
          </Text>{' '}
          by
          {log?.proxyUserName ? (
            <>
              <Text as="span" textStyle={'headingItem'}>
                {' '}
                {log.proxyUserName}
              </Text>{' '}
              on behalf of{' '}
              <Text as="span" textStyle={'headingItem'}>
                {log?.updatedByName}
              </Text>
            </>
          ) : (
            <Text as="span" textStyle={'headingItem'}>
              {' '}
              {log?.updatedByName}
            </Text>
          )}
          {log?.changedFields?.length >= 1 && (
            <Badge ml={2} size="context">
              {log?.changedFields?.length}{' '}
              {log?.changedFields?.length === 1 ? 'Change' : 'Changes'}
            </Badge>
          )}
          {Boolean(log?.deactivatedAccountDetails?.length) && (
            <Badge ml={2} size="context">
              {Number(log?.deactivatedAccountDetails?.length) + 1}{' '}
              {Number(log?.deactivatedAccountDetails?.length) + 1 === 1
                ? 'Profile'
                : 'Profiles'}
            </Badge>
          )}
        </Box>

        {expanded &&
          (log?.action === 'PROFILE_MERGED' ? (
            <>
              <OutlineCard borderless={!hasJournalName}>
                <DuplicateAccounts
                  deactivatedAccounts={log.deactivatedAccountDetails}
                  primaryAccount={log.primaryAccountDetails}
                />
              </OutlineCard>
            </>
          ) : (
            <OutlineCard borderless={!hasJournalName}>
              {log?.journalName && (
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  bgColor="actionSecondaryFill"
                  px={2}
                  py={2}
                  gap={3}
                  flexWrap="wrap"
                  borderTopRadius={'base'}
                >
                  <Flex
                    alignItems="center"
                    gap={2}
                    flexWrap="wrap"
                    minWidth={0}
                  >
                    <Text textStyle="headingItem" noOfLines={2}>
                      {log.journalName}
                    </Text>
                  </Flex>
                </Flex>
              )}
              <VStack spacing={0} align="stretch">
                {log?.changedFields?.map((field, index, arr) => {
                  const Component =
                    componentMap[field?.field as keyof typeof componentMap];
                  const currentValue =
                    field?.currentValue as IResearchSpecialtyKeyword | null;
                  const previousValue =
                    field?.previousValue as IResearchSpecialtyKeyword | null;
                  const hasSpecialtyKeywords =
                    (currentValue?.specialtyAndKeywords?.length ?? 0) > 0 ||
                    (previousValue?.specialtyAndKeywords?.length ?? 0) > 0;
                  const isFirst = index === 0;
                  const isLast = index === arr.length - 1;

                  if (field?.field === 'researchFocusAndExpertise') {
                    return (
                      <OutlineCard
                        p={hasSpecialtyKeywords ? 4 : 0}
                        borderless={!hasSpecialtyKeywords}
                      >
                        {hasSpecialtyKeywords && (
                          <>
                            <Box pb={4}>
                              <RenderResearchSpecialtyKeyword
                                value={
                                  field?.previousValue as IResearchSpecialtyKeyword
                                }
                                label={field?.label || ''}
                                badge="Previous"
                              />
                            </Box>
                            <Box pb={4}>
                              <RenderResearchSpecialtyKeyword
                                value={
                                  field?.currentValue as IResearchSpecialtyKeyword
                                }
                                label={field?.label || ''}
                                badge="Current"
                              />
                            </Box>
                          </>
                        )}
                        {Boolean(
                          (field?.previousValue as IResearchSpecialtyKeyword)
                            ?.reviewerInterest ||
                            (field?.currentValue as IResearchSpecialtyKeyword)
                              ?.reviewerInterest
                        ) && (
                          <Box mt={2}>
                            <ChangeCells hasJournalName={false} isFirst isLast>
                              <RenderField
                                value={
                                  (
                                    field?.previousValue as IResearchSpecialtyKeyword
                                  )?.reviewerInterest
                                }
                                label="Reviewer Interest"
                                badge="Previous"
                              />
                              <RenderField
                                value={
                                  (
                                    field?.currentValue as IResearchSpecialtyKeyword
                                  )?.reviewerInterest
                                }
                                label="Reviewer Interest"
                                badge="Current"
                              />
                            </ChangeCells>
                          </Box>
                        )}
                        {Boolean(
                          (field?.previousValue as IResearchSpecialtyKeyword)
                            ?.professionalInterests?.length ||
                            (field?.currentValue as IResearchSpecialtyKeyword)
                              ?.professionalInterests?.length
                        ) && (
                          <Box mt={2}>
                            <ChangeCells hasJournalName={false} isFirst isLast>
                              <RenderField
                                value={
                                  (
                                    field?.previousValue as IResearchSpecialtyKeyword
                                  )?.professionalInterests
                                }
                                label="Professional Interest"
                                badge="Previous"
                              />
                              <RenderField
                                value={
                                  (
                                    field?.currentValue as IResearchSpecialtyKeyword
                                  )?.professionalInterests
                                }
                                label="Professional Interest"
                                badge="Current"
                              />
                            </ChangeCells>
                          </Box>
                        )}
                      </OutlineCard>
                    );
                  }
                  if (!Component) return null;

                  return (
                    <Box key={field?.field}>
                      <ChangeCells
                        hasJournalName={hasJournalName}
                        isFirst={isFirst}
                        isLast={isLast}
                      >
                        <Component
                          label={field?.label || ''}
                          badge="Previous"
                          value={field?.previousValue as string}
                          affiliation={
                            field?.previousValue as Partial<IAffiliationDetails>
                          }
                        />
                        <Component
                          label={field?.label || ''}
                          badge="Current"
                          value={field?.currentValue as string}
                          affiliation={
                            field?.currentValue as Partial<IAffiliationDetails>
                          }
                        />
                      </ChangeCells>
                    </Box>
                  );
                })}
              </VStack>
            </OutlineCard>
          ))}
      </Box>
    </Flex>
  );
}
